import { Column, CustomSummaryType, ICellFormatter, QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';

import { BehaviorSubject } from 'rxjs';
import { IEntityMetaForeignKeyDto } from 'src/app/api';
import { CerGridEditService } from '../cer-grid/cer-grid-edit.service';
import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerUiLinkType } from '@form/cer-form-navigation.service';
import { CerFormPaneType } from '@form/cer-form-panes/cer-form-panes.service';
import { CerRouteDataEntity } from '@app/app.module-generated';
import { Tooltip } from '@syncfusion/ej2-angular-popups';
import { ParentDataArgs } from '@cer/cer-form/cer-form.service';

export type FieldFormatType =
  'Text' | 'TextRight' | 'TextCenter' | 'TextArea' |
  'Number' | 'Number0' | 'NumberPct' | 'NumberLeft' |
  'Amount' | 'Amount0' | 'AmountPct' | 'AmountPct0' |
  'Integer' | 'IntegerPct' | 'IntegerColor' |
  'DateShort' | 'DateLong' | 'DateMonthYear' | 'DateYear' |
  'DateTime' |
  'CheckBox' | 'CheckBoxInt' |
  'Email' | 'Phone' | 'Url' | 'UrlBase' |
  'Chat' | 'Chip' | 'Chips' | 'Custom' |
  'NoUpDown';

export const FieldFormatBaseString: FieldFormatType[] = [
  'Text', 'TextRight', 'TextArea',
  'Email', 'Phone', 'Url', 'UrlBase'
];
export const FieldFormatBaseNumber: FieldFormatType[] = [
  'Number', 'Number0', 'NumberPct', 'NumberLeft',
  'Amount', 'Amount0', 'AmountPct', 'AmountPct0',
  'Integer', 'IntegerPct', 'IntegerColor'
];
export const FieldFormatBaseDate: FieldFormatType[] = ['DateShort', 'DateLong', 'DateMonthYear', 'DateTime'];
export const FieldFormatBaseBool: FieldFormatType[] = ['CheckBox', 'CheckBoxInt'];

export enum FieldWidthEnum {
  XXXS = 40,
  XXXPS = 50,
  XXS = 60,
  XS = 70,
  S = 85,
  M = 95,
  L = 100,
  XL = 110,
  XXL = 120,
  XXXL = 130,
  XXXXL = 200
}

export type ForiegnKeyColumWidthType = '60px' | '80px' | '100px' | '120px' | '140px' | '140px' | '200px' | '220px' | '240px' | '280px' | '300px' | '320px' | '380px' | '400px' | '420px';

export type BaseFieldType = 'id' | 'num' | 'name' | 'sorting' | 'history';
export type DataSourceType = 'datamanager' | 'webapi' | 'custom';
export type PredicateOperator = "in" | "contains" | "equal" | "notequal" | "lessthanorequal" | "greaterthanorequal" | "lessthan" | "greaterthan" | 'startswith';

export class KeyValue {
  key: string;
  value: string;
}

export class NavigationCommandMetadata {
  link: CerUiLinkType | CerUiLinkType[];
  linkMenu?: string;
  paneTarget?: CerFormPaneType;
  title?: string;
  url?: string;
  command?: UiCommand;
  foreignKey?: IEntityMetaForeignKeyDto;
}

export class ViewMetadata {
  public name?: string;
  public dataSource?: DataSourceType = 'datamanager';
  public data?: object[];
  public offline?: boolean = false;
  public dataOnParentDataOnly?: boolean = false;
  public entityName?: string;
  public dataApiName?: string;
  public dataApiUrl?: string;
  public dataApiParams?: string[] | KeyValue[];
  public numberSeq?: string;
  public primaryKey?: string | string[] = 'id';
  public primaryKeyDb?: string | string[];
  public primaryType?: ForeignKeyType;
  public parentPrimaryKey?: string;
  public parentForeignKey?: string;
  public text?: string;
  public textSingular?: string;
  public titleFields?: string[];
  public allowEdit?: boolean;
  public allowCreate?: boolean;
  public allowDelete?: boolean;
  public allowSorting?: boolean;
  public allowFiltering?: boolean;
  public baseFields?: BaseFieldType[];
  public exportFileName?: string;
  public insertOverride?: any;
  public onAdd?: any;
  public navigations?: NavigationCommandMetadata[];
  public onParentValueGet?: (e: ParentDataArgs) => string;
  public onBeforeNavigations?: (e: NavigationCommandQuery) => NavigationCommandMetadata[];
  public attachmentUploadUrl?: (e: ViewMetadata, overwriteExisting: boolean) => string;
  public fieldMetaData?: FieldMetadata[];
}

export class TabMetadata {
  public idx: number;
  public name: string;
  public text: string;
  public icon?: string;
}

export class FieldGroupMetadata {
  public idx?: number;
  public name: string;
  public text: string;
  public tabIdx?: number;
  public width?: number;
  public default?: boolean;
  public fields: string[];
}

export class FieldEditClickedArgs {
  cancel: boolean
  field: string;
  fieldMeta: FieldMetadata;
  data: any;
  value: any;
}


export class FieldEditChangingArgs {
  cancel: boolean
  field: string;
  fieldMeta: FieldMetadata;
  editService?: any;
  dataPrevious: any;
  data: any;
  valuePrevious: any;
  value: any;
}

export class FieldEditChangedArgs {
  field: string;
  fieldMeta: FieldMetadata;
  fieldsMeta: FieldMetadata[];
  editService?: CerGridEditService;
  data: any;
  itemData?: any;
  value: any;
  previousValue: any;
  previousData?: any;
  args?: any;
}

const fieldTypeNames = [
  'id', 'num', 'name_', 'sorting', 'created', 'modified', 'createdBy', 'modifiedBy',
  'email', 'phone', 'mobile', 'active',
  'dataTable', 'dataTagType', 'tags', 'documentLib',
  'comments',
  'language', 'country', 'currency', 'currencyId',
  'transDate',
  'product', 'productLookup',
  'productList',
  'productGroup',
  'productComposition',
  'productForm',
  'productFormDetail',
  'productPublishingStatus',
  'productMarketAvailability',
  'productSourcing',
  'productSupplier',
  'productDistributor',
  'productType',
  'party', 'partyId', 'partyRole',
  'vendor', 'vendorGroup', 'vendorTransType', 'vendorTaxGroup',
  'paymentTerm',
  'financeTaxCode',
  'financeLedgerAccount',
  'financeProject',
  'user', 'userShortName', 'role',
  'documentLibFolder',
  'blobStorage'
] as const;

export type FieldType = typeof fieldTypeNames[number];

const foreignKeyTypeNames = [
  'dataTable', 'dataTagType', 'tags',
  'language', 'country', 'currency', 'currencyId',
  'product',  'productLookup', 'refDataProduct', 'productGroup', 'productCategory',
  'productProject',
  'productList',
  'productGroup',
  'productComposition',
  'productForm',
  'productFormDetail',
  'productPublishingStatus',
  'productMarketAvailability',
  'productSupplier',
  'productDistributor',
  'productType',
  'party', 'partyId', 'partyRole',
  'vendor', 'vendorView', 'vendorGroup', 'vendorTransType', 'vendorTaxGroup',
  'paymentTerm',
  'financeTaxCode',
  'financeLedgerAccount',
  'financeProjectCostType',
  'financeProject',
  'voucherApprovalStatus',
  'voucherCategory',
  'warehouseOutboundOrderLookup',
  'forfatter',
  'documentLibFolder',
  'user', 'userShortName'
] as const;

export class UiCommandRenderCell {
  //public args: QueryCellInfoEventArgs;
  public data: any;
  public column: Column;
  public fieldMeta: FieldMetadata;
  public columnName: string;
  public cell: any;
  public foreignKeyData: boolean;
  public value?: any;
  public style: any;
  public colSpan: number = 1;
  public rowSpan: number = 1;
  public isExcel: boolean = false;
}

export type ForeignKeyType = typeof foreignKeyTypeNames[number];

export type FieldTooltipLocation = 'Header' | 'Content' | 'Footer';

export type OrderByDirection = "None" | "Ascending" | "Descending";

export class FieldMetaDataForeignDataOptions {
  public maintainFromRowNavigation?: boolean;
  public subject?: BehaviorSubject<Object[]>;
  public cache?: 'dynamic' | 'nocache';
}

export class FieldMetaDataForeignKey {
  public data?: any[];
  public dataOptions?: FieldMetaDataForeignDataOptions;
  public navigationName?: string;
  public navigations?: NavigationCommandMetadata[];
  public onBeforeNavigations?: (e: NavigationCommandQuery) => NavigationCommandMetadata[];
}

export class FieldMetadata {
  public fieldType?: FieldType;
  public name?: string;
  public text?: string;
  public tooltipText?: string;
  public tooltipShow?: FieldTooltipLocation[];
  public required?: boolean;
  public validationMaxLen?: number;
  public validationRules?: object;
  public allowEdit?: boolean;
  public allowEditOnAdd?: boolean;
  public allowFiltering?: boolean;
  public allowFilteringSearchBox?: boolean;
  public allowSorting?: boolean;
  public allowSearching?: boolean;
  public allowColumnChooser?: boolean;
  public allowClear?: boolean;
  public defaultValue?: any;
  public isIdentity?: boolean;
  public visible?: boolean;
  public visibleEdit?: boolean;
  public visibleAdd?: boolean;
  public visibleExport?: boolean;
  public orderExport?: number;
  public width?: string;
  public widthExport?: number;
  public widthType?: FieldWidthEnum;
  public minWidth?: string;
  public maxWidth?: string;
  public maxWidthType?: FieldWidthEnum;
  public format?: FieldFormatType | FieldFormatType[];
  public formatClass?: string;
  public formatter?: ICellFormatter;
  public formatCustom?: string;
  public cellAttributes?: { [x: string]: Object; };
  public template?: string;
  public headerTemplate?: string;
  public editTemplate?: string;
  public foreignKeyType?: ForeignKeyType;
  public foreignKey?: FieldMetaDataForeignKey;
  public foreignKeyTableName?: string;
  public foreignKeyTableParams?: string[];
  public foreignKeyFromNavigationName?: string;
  public foreignKeyField?: string;
  public foreignKeyValue?: string;
  public foreignKeySelectFields?: string[];
  public foreignKeyColumns?: string[];
  public foreignKeyColumnsWidth?: ForiegnKeyColumWidthType[];
  public foreignKeyTitles?: string[];
  public foreignKeySearchOnEmptyFocus?: boolean;
  public foreignKeySearchFields?: string[];
  public foreignKeySearchOperators?: PredicateOperator[];
  public foreignKeySortFields?: string[];
  public foreignKeyAllowFiltering?: boolean;
  public foreignKeyFilterFields?: string[];
  public foreignKeyFilterOperators?: PredicateOperator[];
  public foreignKeyFilterValues?: any[];
  public foreignKeyUsing?: string;
  public popupHeight?: string;
  public popupWidth?: string;
  public orderBy?: OrderByDirection;
  public orderByPriority?: number;
  public groupBy?: boolean;
  public filterOperator?: PredicateOperator;
  public filterValue?: any;
  public filterOnClient?: boolean;
  public urlBase?: string;
  public footerType?: "Sum" | "Custom";
  public footerFunction?: CustomSummaryType;
  public footerTemplate?: string;
  public onClicked?: (e: FieldEditClickedArgs) => void;
  public onEditChanging?: (e: FieldEditChangingArgs) => void;
  public onCellSelected?: (e: UiCommandEvent) => void;
  public onEditChanged?: (e: FieldEditChangedArgs) => void;
  public onKeyDown?: (e: FieldEditChangedArgs) => void;
  public onRenderCell?: (e: UiCommandRenderCell) => void;
  public onExcelCell?: (e: UiCommandRenderCell) => void;
  public onTagging?: (e: any) => void;
  public onQueryCellInfo?: (e: QueryCellInfoEventArgs) => void;

  // State fields when edit is active
  public currentRowVisible?: boolean;
  public currentRowAllowEdit?: boolean;

}

export class ViewMetadataUtil {

  public static initSingleRow(v: ViewMetadata) {
    v.allowFiltering = false;
    v.allowSorting = false;
  }

  public static linkIsUniqueRow(v: ViewMetadata, entities: CerRouteDataEntity[] = null) {
    const linkFieldName = ViewMetadataUtil.linkFieldNameGet(v);
    const pkFieldName = ViewMetadataUtil.pkFieldNameGet(v);
    var isUniqueRow = (linkFieldName?.length > 0 && linkFieldName == pkFieldName);
    if (entities?.length > 0) {
      const keys = entities.find(f => f.name == v.dataApiName)?.keys;
      if (keys?.length > 0) {
        isUniqueRow = (keys.indexOf(linkFieldName) >= 0);
      }
    }
    return isUniqueRow;
  }

  public static fieldValueInitFromParent(v: ViewMetadata, data: any, parentData: any) {
    if (data && parentData && v) {
      const linkFieldName = ViewMetadataUtil.linkFieldNameGet(v);
      const linkParentFieldName = ViewMetadataUtil.linkParentFieldNameGet(v);

      if (linkFieldName?.length > 0 &&
        linkParentFieldName?.length > 0 &&
        parentData[linkParentFieldName]) {
        data[linkFieldName] = parentData[linkParentFieldName];
      }
    }
  }

  public static linkParentFieldNameGet(v: ViewMetadata): string {
    if (v?.parentForeignKey?.length > 0 && v?.parentPrimaryKey?.length > 0) {
      return v.parentPrimaryKey;
    }
    return null;
  }

  public static pkFieldNameGet(v: ViewMetadata, fromDb: boolean = false): string | string[] {
    if (fromDb && v?.primaryKeyDb?.length > 0) {
      return v.primaryKeyDb;
    }
    return v?.primaryKey;
  }

  public static pkSingleFieldNameGet(v: ViewMetadata, fromDb: boolean = false): string {
    var pkName = ViewMetadataUtil.pkFieldNameGet(v, fromDb);
    if (pkName != null && Array.isArray(pkName)) {
      return pkName.length > 0 ? pkName[0] : null;
    }
    return pkName as string;
  }

  public static linkFieldNameGet(v: ViewMetadata): string {
    return v?.parentForeignKey;
  }

  public static linkIsParentLinkDefined(v: ViewMetadata): boolean {
    return ViewMetadataUtil.linkParentFieldNameGet(v)?.length > 0 ||
          (v.onParentValueGet != null);
  }

  public static parentValueGet(v: ViewMetadata, parentData: ParentDataArgs): any {
    if (v.onParentValueGet != null) {
      return v.onParentValueGet(parentData);
    }
    const parentKey = ViewMetadataUtil.linkParentFieldNameGet(v);
    if (parentKey?.length > 0 && parentData?.row != null) {
      return <any>parentData.row[parentKey];
    }
    return null;
  }
}

export class ClassCss {
  public static add(element: HTMLElement, className: string) {
    if (element != null && !element.classList.contains(className)) {
      element.classList.add(className);
    }
  }
}

export type ColorCssName = 'red' | 'yellow' | 'green' | 'blue' |
  'light-red' | 'light-green' | 'light-blue';

export class ColorCss {

  public static value(colorName: ColorCssName): string {
    return `var(--color-cer-${colorName})`;
  }

  public static backgroundClassValue(colorName: ColorCssName): string {
    return `backgroundcolor-cer-${colorName}`;
  }

  public static colorClassValue(colorName: ColorCssName) {
    return `color-cer-${colorName}`;
  }

  public static backgroundClass(ele: HTMLElement, colorName: ColorCssName) {
    if (ele) {
      ClassCss.add(ele, ColorCss.backgroundClassValue(colorName));
    }
  }

  public static colorClass(ele: HTMLElement, colorName: ColorCssName) {
    if (ele) {
      ClassCss.add(ele, ColorCss.colorClassValue(colorName));
    }
  }

  public static backgroundStyle(ele: HTMLElement, colorName: ColorCssName) {
    if (ele) {
      ele.style.backgroundColor = ColorCss.value(colorName);
    }
  }

  public static colorStyle(ele: HTMLElement, colorName: ColorCssName) {
    if (ele) {
      ele.style.color = ColorCss.value(colorName);
    }
  }
}



export class ColorExcel {
  private static colors: Map<ColorCssName, string> = new Map([
    ['red', "#FF7070"],
    ['yellow', "#FFFF70"],
    ['green', "#70FF70"],
    ['blue', "#7070FF"],
    ['light-red', "#FF7070"],
    ['light-green', "#70FF70"],
    ['light-blue', "7070FF"]
  ]);

  public static getByName(colorName: ColorCssName): string {
    return ColorExcel.colors.get(colorName);
  }
}

export class RenderCell {
  public static createTooltip(e: UiCommandRenderCell, c: string) {
    if (c?.length > 0) {
      if (e.isExcel) {
        c = c.replaceAll(" ", "_").replaceAll("/", ":").replaceAll("\\", ":");
        e.cell.hyperlink = {
          target: c
        };
      } else {
        new Tooltip({ content: c }, e.cell as HTMLElement);
      }
    }
  }

  public static backgroundColor(e: UiCommandRenderCell, colorName: ColorCssName, isExcel: boolean = false) {
    if (e.isExcel) {
      e.style ??= {};
      e.style.backColor = ColorExcel.getByName(colorName);
    }
    else {
      RenderCell.elementBackgroundColor(e.cell, colorName);
    }
  }

  public static elementBackgroundColor(element: HTMLElement, colorName: ColorCssName) {
    ColorCss.backgroundClass(element, colorName);
  }

  public static elementBackgroundColorHighLight(element: HTMLElement) {
    ClassCss.add(element, 'e-highlight');
  }

  public static elementBackgroundColorHighLightAlt(element: HTMLElement) {
    ClassCss.add(element, 'e-highlight-alt');
  }

  public static elementClassAdd(element: HTMLElement, className: string) {
    ClassCss.add(element, className);
  }
}

export class FieldMetadataUtil {

  static mergeByName(fieldName: string, fields: FieldMetadata[], extras: FieldMetadata) {
    const f: FieldMetadata = FieldMetadataUtil.fieldByName(fieldName, fields);
    if (f != null && extras != null) {
      Object.entries(extras).forEach(p => {
        const propName = p[0];
        const propValue = p[1];
        if (propValue != null) {
          (<any>f)[propName] = propValue;
        }
      });
    }
  }

  static merge(base: FieldMetadata, extras: FieldMetadata) {
  }

  static fieldByCommandEvent(event: UiCommandEvent): FieldMetadata {
    if (event.columnName?.length > 0 && event.viewMetadata?.fieldMetaData) {
      return FieldMetadataUtil.fieldByName(event.columnName, event.viewMetadata.fieldMetaData);
    }
    return null;
  }

  public static value(rowData: any, fieldMeta: FieldMetadata): string {
    return rowData && fieldMeta?.name ? rowData[fieldMeta.name] : null;
  }
  public static valueSet(rowData: any, value: any, fieldMeta: FieldMetadata) {
    if (rowData && fieldMeta?.name.length > 0) {
      rowData[fieldMeta.name] = value;
    }
  }

  public static valueGet<T>(rowData: any, fieldMeta: FieldMetadata): T {
    return <T><any>FieldMetadataUtil.value(rowData, fieldMeta);
  }

  public static formatValue(displayValue: any, fieldMeta: FieldMetadata): string {
    return displayValue?.toString();
  }

  public static fieldByName(fieldName: string, fieldMeta: FieldMetadata[]): FieldMetadata {
    return fieldName?.length > 0 ? fieldMeta.find(f => f.name == fieldName) : null;
  }

  public static fieldByText(text: string, fieldMeta: FieldMetadata[]): FieldMetadata {
    return text?.length > 0 ? fieldMeta.find(f => f.text == text) : null;
  }

  public static foreignKeyFieldGet(fieldMeta: FieldMetadata): string {
    return fieldMeta?.foreignKeyField ?? 'id';
  }

  public static fieldMetaByColumn(column: any): FieldMetadata {
    return column?.meta;
  }

  public static hasFormat(format: FieldFormatType, fieldMeta: FieldMetadata): boolean {
    if (fieldMeta?.format == null) {
      return false;
    }
    if (Array.isArray(fieldMeta.format)) {
      return fieldMeta.format.includes(format);
    }
    else {
      return format == fieldMeta.format;
    }
  }

}

export class NavigationCommandQuery {
  public viewMeta: ViewMetadata;
  public fieldMeta: FieldMetadata;
  public rowData: any;
  public rowDataSelected: any;
}

export class FieldTypeSettings {
  public static id: FieldMetadata = { name: 'id', text: 'Id', tooltipText: 'Internt id for posten', allowEdit: false, allowEditOnAdd: false, format: 'Integer', visible: false, widthType: FieldWidthEnum.S };
  public static num: FieldMetadata = { name: 'num', text: 'Kode', tooltipText: 'Kode for posten', allowEdit: false, allowEditOnAdd: true, required: true, format: 'Text', visible: true, widthType: FieldWidthEnum.XS, orderBy: 'Ascending', orderByPriority: 99 };
  public static name_: FieldMetadata = { name: 'name', text: 'Navn', tooltipText: 'Navn for posten', allowEdit: true, required: true, format: 'Text', visible: true, widthType: FieldWidthEnum.M };
  public static sorting: FieldMetadata = { name: 'sorting', text: 'Sortering', tooltipText: 'Standard sortering', allowEdit: true, required: true, format: 'Integer', visible: true, widthType: FieldWidthEnum.S, orderBy: 'Ascending', orderByPriority: 90 };
  public static created: FieldMetadata = { name: 'created', text: 'Oprettet', tooltipText: 'Oprettet dato', allowEdit: false, allowEditOnAdd: false, format: 'DateLong', visible: false };
  public static modified: FieldMetadata = { name: 'modified', text: 'Ændret', tooltipText: 'Ændret dato', allowEdit: false, allowEditOnAdd: false, format: 'DateLong', visible: false };
  public static createdBy: FieldMetadata = { name: 'createdBy', text: 'Oprettet af', tooltipText: 'Oprettet af brugernavn', fieldType: 'user', allowEdit: false, allowEditOnAdd: false, widthType: FieldWidthEnum.S, visible: false };
  public static modifiedBy: FieldMetadata = { name: 'modifiedBy', text: 'Ændret af', tooltipText: 'Ændret af brugernavn', fieldType: 'user', allowEdit: false, allowEditOnAdd: false, widthType: FieldWidthEnum.S, visible: false };
  public static email: FieldMetadata = { name: 'email', text: 'E-mail', format: 'Email' };
  public static phone: FieldMetadata = { name: 'phone', text: 'Telefon', format: 'Phone' };
  public static mobile: FieldMetadata = { name: 'mobile', text: 'Mobil', format: 'Phone' };
  public static active: FieldMetadata = { name: 'active', text: 'Aktiv', format: 'CheckBox', widthType: FieldWidthEnum.XS };
  public static dataTable: FieldMetadata = { name: 'table', text: 'Tabel', foreignKeyType: 'dataTable', };
  public static dataTagType: FieldMetadata = { name: 'tag', text: 'Kategori', foreignKeyType: 'dataTagType', };
  public static tags: FieldMetadata = { name: 'tags', text: 'Kategorier', format: 'Chips', foreignKeyType: 'tags' };
  public static documentLib: FieldMetadata = { name: 'documentLib', text: 'Dokumentarkiv', foreignKeyTableName: 'DocumentLib', foreignKeyField: 'id' };
  public static comments: FieldMetadata = { name: 'comments', text: 'Noter', tooltipText: 'Fritekst noter', format: "TextArea", widthType: FieldWidthEnum.XXXL };
  public static language: FieldMetadata = { name: 'language', text: 'Sprog', tooltipText: 'Sprogkode', defaultValue: 'dan', width: '70px', foreignKeyType: 'language', widthType: FieldWidthEnum.XXXS };
  public static country: FieldMetadata = { name: 'country', text: 'Land', tooltipText: 'Landekode', defaultValue: 'dk', foreignKeyType: 'country', widthType: FieldWidthEnum.XS };
  public static currency: FieldMetadata = { name: 'currency', text: 'Valuta', tooltipText: 'Valutakode', defaultValue: 'DKK', foreignKeyType: 'currency', widthType: FieldWidthEnum.XXXS };
  public static currencyId: FieldMetadata = { name: 'currency', text: 'Valuta', tooltipText: 'Valutakode', foreignKeyType: 'currencyId', widthType: FieldWidthEnum.XXXS };
  public static transDate: FieldMetadata = { name: 'transDate', text: 'Dato', format: 'DateShort' };
  public static product: FieldMetadata = { name: 'product', text: 'ISBN', tooltipText: 'ISBN på titel', foreignKeyType: 'product', widthType: FieldWidthEnum.L };
  public static productLookup: FieldMetadata = { name: 'product', text: 'ISBN', tooltipText: 'ISBN på titel', foreignKeyType: 'productLookup', widthType: FieldWidthEnum.L };
  public static productList: FieldMetadata = { name: 'productList', text: 'Titelliste', foreignKeyType: 'productList', widthType: FieldWidthEnum.S };
  public static productCategory: FieldMetadata = { name: 'productCategory', text: 'Varegruppe (boghandler)', width: '100px', foreignKeyType: 'productCategory' };
  public static productGroup: FieldMetadata = { name: 'group', text: 'Gruppe', width: '100px', foreignKeyType: 'productGroup' };
  public static productComposition: FieldMetadata = { name: 'productComposition', text: 'Udformning', width: '100px', foreignKeyType: 'productComposition' };
  public static productForm: FieldMetadata = { name: 'productForm', text: 'Indbinding', width: '100px', foreignKeyType: 'productForm' };
  public static productFormDetail: FieldMetadata = { name: 'productFormDetail', text: 'Detaljer indbinding ', width: '100px', foreignKeyType: 'productFormDetail' };
  public static productPublishingStatus: FieldMetadata = { name: 'publishingStatus', text: 'Udgivelsesstatus', width: '100px', foreignKeyType: 'productPublishingStatus' };
  public static productMarketAvailability: FieldMetadata = { name: 'supplyMarketAvailability', text: 'Tilgængelighed marked', width: '100px', foreignKeyType: 'productMarketAvailability' };
  public static productSourcing: FieldMetadata = { name: 'productSourcing', text: 'Overvågning', width: '100px', foreignKeyType: 'productSupplier' };
  public static productSupplier: FieldMetadata = { name: 'productSupplier', text: 'Trykkeri', width: '100px', foreignKeyType: 'productSupplier' };
  public static productDistributor: FieldMetadata = { name: 'productDistributor', text: 'Ekspedition', width: '60px', foreignKeyType: 'productDistributor' };
  public static productType: FieldMetadata = { name: 'productType', text: 'Type', foreignKeyType: 'productType' };
  public static party: FieldMetadata = { name: 'party', text: 'Kontakt', tooltipText: 'Kontakt detaljer', foreignKeyType: 'party' };
  public static partyId: FieldMetadata = { name: 'party', text: 'Kontakt', tooltipText: 'Kontakt i adressekartotek', foreignKeyType: 'partyId' };
  public static partyRole: FieldMetadata = { name: 'partyRole', text: 'Rolle', tooltipText: 'Rolle for kontakten', foreignKeyType: 'partyRole' };
  public static vendor: FieldMetadata = { name: 'num', text: 'Kreditor', tooltipText: 'Kreditor konto', foreignKeyType: 'vendorView' };
  public static vendorGroup: FieldMetadata = { name: 'group', text: 'Gruppe', tooltipText: 'Kreditorgruppe', foreignKeyType: 'vendorGroup' };
  public static vendorTransType: FieldMetadata = { name: 'transType', text: 'Type', tooltipText: 'Kreditorposttype', foreignKeyType: 'vendorTransType' };
  public static vendorTaxGroup: FieldMetadata = { name: 'taxGroup', text: 'Momskode', foreignKeyType: 'vendorTaxGroup' };
  public static paymentTerm: FieldMetadata = { name: 'paymentTerm', text: 'Betalingsbetingelse', foreignKeyType: 'paymentTerm' };
  public static financeTaxCode: FieldMetadata = { name: 'taxCode', text: 'Momskode', foreignKeyType: 'financeTaxCode' };
  public static financeProject: FieldMetadata = { name: 'project', text: 'Projekt', tooltipText: 'Projekt', foreignKeyType: 'financeProject', widthType: FieldWidthEnum.S };
  public static financeLedgerAccount: FieldMetadata = { name: 'ledgerAccount', text: 'Konto', tooltipText: 'Finans konto', foreignKeyType: 'financeLedgerAccount' };
  public static user: FieldMetadata = { name: 'user', text: 'Bruger', foreignKeyType: 'user' };
  public static userShortName: FieldMetadata = { name: 'user', text: 'Bruger', foreignKeyType: 'userShortName' };
  public static role: FieldMetadata = { name: 'role', text: 'Rolle', foreignKeyTableName: 'Role', foreignKeyField: 'id' };
  public static documentLibFolder: FieldMetadata = { name: 'documentLibFolder', text: "Folder dokumentarkiv", widthType: FieldWidthEnum.M, foreignKeyType: 'documentLibFolder' };
  public static blobStorage: FieldMetadata = { name: 'blobStorage', text: 'Lager', widthType: FieldWidthEnum.M, foreignKeyTableName: 'BlobStorage', foreignKeyField: 'id' };
};

const ProductLookupFK: FieldMetadata = {
  foreignKey: {
    navigations: [
      { link: ['navMenu', 'fk'], foreignKey: { parentEntity: 'RefDataProduct', parentColumn: 'num' } },
      { link: 'navMenu', foreignKey: { parentEntity: 'WarehouseOnhandStatus', parentColumn: 'product' } }
    ]
  }
};

const RefDataProductFK: FieldMetadata = {
  foreignKeyTableName: 'RefDataProductLookup',
  foreignKeyField: 'num',
  foreignKeyValue: 'num',
  foreignKeyAllowFiltering: true,
  foreignKeyColumns: ['num', 'name', 'type'],
  foreignKeyTitles: ['ISBN', 'Titel', 'Type'],
  foreignKeyColumnsWidth: ['120px', '380px', '100px'],
  foreignKeySearchFields: ['num', 'name'],
  foreignKeySearchOperators: ['contains', 'contains'],
  popupWidth: '620px', popupHeight: '400px',
  foreignKey: ProductLookupFK.foreignKey
};

const RefDataProductProjectFK: FieldMetadata = {
  foreignKeyTableName: 'RefDataProductLookup',
  foreignKeyField: 'num',
  foreignKeyValue: 'num',
  foreignKeyAllowFiltering: true,
  foreignKeyColumns: ['num', 'projectNum', 'name', 'type'],
  foreignKeyTitles: ['ISBN', 'Projekt', 'Titel', 'Type'],
  foreignKeyColumnsWidth: ['120px', '80px', '300px', '100px'],
  foreignKeySearchFields: ['num', 'projectNum', 'name'],
  foreignKeySearchOperators: ['contains', 'contains', 'contains'],
  popupWidth: '620px', popupHeight: '400px',
  foreignKey: ProductLookupFK.foreignKey
}

const ProductFK: FieldMetadata = {
  foreignKeyTableName: 'RefDataProduct',
  foreignKeyField: 'num',
  foreignKeyValue: 'num',
  foreignKeyColumns: ['num', 'name', 'authors'],
  foreignKeyTitles: ['ISBN', 'Titel', 'Forfatter'],
  foreignKeyAllowFiltering: true,
  foreignKeyColumnsWidth: ['120px', '380px', '200px'],
  popupWidth: '620px', popupHeight: '400px',
  foreignKeySearchFields: ['num', 'name'],
  foreignKeySearchOperators: ['contains', 'contains'],
  foreignKey: ProductLookupFK.foreignKey
};

const WarehouseOutboundOrderLookupFK: FieldMetadata = {
  foreignKey: {
    navigations: [
      { link: ['navMenu', 'fk'], foreignKey: { parentEntity: 'WarehouseOutboundOrder', parentColumn: 'num' } },
    ]
  }
};

export class ForeignKeyTypeSettings {
  public static dataTable: FieldMetadata = { foreignKeyTableName: 'DataTable', foreignKeyField: 'id', foreignKeyValue: 'name', foreignKeyColumns: ['name'], foreignKeyTitles: ['Tabelnavn'], foreignKeySearchFields: ['name'], foreignKeyColumnsWidth: ['200px'] };
  public static dataTagType: FieldMetadata = { foreignKeyTableName: 'DataTagType', foreignKeyField: 'tag', foreignKeyValue: 'name', foreignKeyColumns: ['name'], foreignKeyTitles: ['Kategori'], foreignKeySearchFields: ['name'], foreignKeyColumnsWidth: ['200px'] };
  public static tags: FieldMetadata = { allowFilteringSearchBox: false, foreignKeyTableName: 'DataTagType', foreignKeyField: 'id', foreignKeyValue: 'name', foreignKeyColumns: ['id'], foreignKeyTitles: ['Kort', 'Kategori'], foreignKeySearchFields: ['shortName', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static language: FieldMetadata = { foreignKeyTableName: 'Language', foreignKeyField: 'num', foreignKeyValue: 'name', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['60px', '200px'] };
  public static country: FieldMetadata = { foreignKeyTableName: 'Country', foreignKeyField: 'num', foreignKeyValue: 'name', foreignKeyColumns: ['num', 'name', 'nameLocal'], foreignKeyTitles: ['Kode', 'Navn', 'Lokalt navn'], foreignKeySearchFields: ['num', 'name', 'nameLocal'], foreignKeyColumnsWidth: ['60px', '200px', '200px'], foreignKeySortFields: ['active desc'] /*, foreignKeyFilterFields: ['active'], foreignKeyFilterOperators: ['equal'], foreignKeyFilterValues: [true] */ };
  public static currencyId: FieldMetadata = { foreignKeyTableName: 'Currency', foreignKeyField: 'id', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num'], foreignKeyColumnsWidth: ['60px', '200px'] };
  public static currency: FieldMetadata = { foreignKeyTableName: 'Currency', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num'], foreignKeyColumnsWidth: ['60px', '200px'] };
  public static refDataProduct: FieldMetadata = RefDataProductFK;
  public static product: FieldMetadata = ProductFK;
  public static productLookup: FieldMetadata = ProductLookupFK;
  public static productProject: FieldMetadata = RefDataProductProjectFK;
  public static productList: FieldMetadata = { foreignKeyTableName: 'ProductList', foreignKeyField: 'num', foreignKeyValue: 'name', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px', '200px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'] };
  public static productCategory: FieldMetadata = { foreignKeyTableName: 'ProductCategory', foreignKeyField: 'num', foreignKeyValue: 'search', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Gruppe', 'Navn'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'] };
  public static productGroup: FieldMetadata = { foreignKeyTableName: 'ProductGroup', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Gruppe', 'Navn'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'] };
  public static productComposition: FieldMetadata = { foreignKeyTableName: 'ProductComposition', foreignKeyField: 'num', foreignKeyValue: 'onixName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Gruppe', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name', 'onix'], foreignKeySearchOperators: ['contains', 'contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productForm: FieldMetadata = { foreignKeyTableName: 'ProductForm', foreignKeyField: 'num', foreignKeyValue: 'onixName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Gruppe', 'Navn'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name', 'onix'], foreignKeySearchOperators: ['contains', 'contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productFormDetail: FieldMetadata = { foreignKeyTableName: 'ProductFormDetail', foreignKeyField: 'num', foreignKeyValue: 'onixName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Gruppe', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name', 'onix'], foreignKeySearchOperators: ['contains', 'contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productPublishingStatus: FieldMetadata = { foreignKeyTableName: 'ProductPublishingStatus', foreignKeyField: 'num', foreignKeyValue: 'numName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Kode', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name', 'onix'], foreignKeySearchOperators: ['contains', 'contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productMarketAvailability: FieldMetadata = { foreignKeyTableName: 'ProductMarketAvailability', foreignKeyField: 'num', foreignKeyValue: 'numName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Kode', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productSupplier: FieldMetadata = { foreignKeyTableName: 'ProductSupplier', foreignKeyField: 'num', foreignKeyValue: 'numName', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Kode', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productDistributor: FieldMetadata = { foreignKeyTableName: 'ProductDistributor', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'onix'], foreignKeyTitles: ['Kode', 'Navn', 'ONIX'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px', '120px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name', 'onix'], foreignKeySearchOperators: ['contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static productType: FieldMetadata = { foreignKeyTableName: 'ProductType', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'], foreignKeySortFields: ['sorting'] };
  public static party: FieldMetadata = { foreignKeyTableName: 'Party', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'attention'], foreignKeyTitles: ['Kontakt id', 'Navn', 'Attention'], foreignKeySearchFields: ['num', 'name', 'attention'], foreignKeyColumnsWidth: ['100px', '300px', '300px'], foreignKeySelectFields: [] /* All */ };
  public static partyId: FieldMetadata = { foreignKeyTableName: 'Party', foreignKeyField: 'id', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'attention'], foreignKeyTitles: ['Kontakt id', 'Navn', 'Attention'], foreignKeySearchFields: ['num', 'name', 'attention'], foreignKeyColumnsWidth: ['100px', '300px', '300px'] };
  public static partyRole: FieldMetadata = { foreignKeyTableName: 'PartyRole', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static vendor: FieldMetadata = { foreignKeyTableName: 'Vendor', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'currency'], foreignKeyTitles: ['Kode', 'Navn', 'Valuta'], foreignKeySearchFields: ['name', 'currency'], foreignKeyColumnsWidth: ['100px', '300px', '60px'] };
  public static vendorView: FieldMetadata = { foreignKeyTableName: 'VendorView', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'currency'], foreignKeyTitles: ['Kode', 'Navn', 'Valuta'], foreignKeySearchFields: ['name', 'currency'], foreignKeyColumnsWidth: ['100px', '300px', '60px'] };
  public static vendorGroup: FieldMetadata = { foreignKeyTableName: 'VendorGroup', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static vendorTaxGroup: FieldMetadata = { foreignKeyTableName: 'VendorTaxGroup', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static vendorTransType: FieldMetadata = { foreignKeyTableName: 'VendorTransType', foreignKeyField: 'id', foreignKeyValue: 'name', foreignKeyColumns: ['name'], foreignKeyTitles: ['Navn'], foreignKeySearchFields: ['name'], foreignKeyColumnsWidth: ['200px'] };
  public static paymentTerm: FieldMetadata = { foreignKeyTableName: 'PaymentTerm', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'], foreignKeySearchOperators: ['contains', 'contains'] };
  public static financeTaxCode: FieldMetadata = { foreignKeyTableName: 'FinanceTaxCode', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static financeLedgerAccount: FieldMetadata = { foreignKeyTableName: 'FinanceLedgerAccount', foreignKeyField: 'num', foreignKeyValue: 'description', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Konto', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '300px'] };
  public static financeProject: FieldMetadata = { foreignKeyTableName: 'FinanceProject', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name', 'authors'], foreignKeyTitles: ['ISBN', 'Titel', 'Forfatter'], foreignKeyAllowFiltering: true, foreignKeyColumnsWidth: ['120px', '380px', '200px'], popupHeight: '400px', foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains'] };
  public static financeProjectCostType: FieldMetadata = { foreignKeyTableName: 'FinanceProjectCostType', foreignKeyField: 'num', foreignKeyValue: 'description', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Navn'], foreignKeySearchFields: ['num', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static voucherApprovalStatus: FieldMetadata = { foreignKeyTableName: 'VoucherApprovalStatus', foreignKeyField: 'id', foreignKeyValue: 'name', foreignKeyColumns: ['name'], foreignKeyTitles: ['Navn'], foreignKeySearchFields: ['name'], foreignKeyColumnsWidth: ['200px'] };
  public static voucherCategory: FieldMetadata = { foreignKeyTableName: 'VoucherCategory', foreignKeyField: 'id', foreignKeyValue: 'name', foreignKeyColumns: ['name'], foreignKeyTitles: ['Navn'], foreignKeySearchFields: ['name'], foreignKeyColumnsWidth: ['200px'] };
  public static warehouseOutboundOrderLookup: FieldMetadata = WarehouseOutboundOrderLookupFK;
  public static forfatter: FieldMetadata = { foreignKeyTableName: 'Forfatter', foreignKey: { navigations: [{ url: 'contract/party-list', link: 'fk' }] }, foreignKeyField: 'forfatterId', foreignKeyValue: 'forfatterId', foreignKeyColumns: ['forfatterId', 'name'], foreignKeyTitles: ['Forfat.id', 'Navn'], foreignKeySearchFields: ['forfatterId'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static documentLibFolder: FieldMetadata = { foreignKeyTableName: 'DocumentLib', foreignKeyField: 'name' };
  public static user: FieldMetadata = { foreignKeyTableName: 'User', foreignKeyField: 'id', foreignKeyValue: 'shortName', foreignKeyColumns: ['shortName', 'name'], foreignKeyTitles: ['Initialer', 'Navn'], foreignKeySearchFields: ['shortName', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
  public static userShortName: FieldMetadata = { foreignKeyTableName: 'User', foreignKeyField: 'shortName', foreignKeyValue: 'shortName', foreignKeyColumns: ['shortName', 'name'], foreignKeyTitles: ['Initialer', 'Navn'], foreignKeySearchFields: ['shortName', 'name'], foreignKeyColumnsWidth: ['100px', '200px'] };
};

export class FieldCell {

  public static cellInfoValue(e: QueryCellInfoEventArgs) {
    const colName = e.column?.field;
    return colName?.length > 0 ? (<any>e.data)[colName] : null;
  }
}

export class FieldMetaDefaults {

  static init(meta: FieldMetadata, resetTypeSetup: boolean = false): FieldMetadata {
    if (meta.fieldType != null) {
      var typeSetup: FieldMetadata = FieldTypeSettings[meta.fieldType];
      if (typeSetup != null) {
        FieldMetaDefaults.mergeDefault(typeSetup, meta);
        if (resetTypeSetup) {
          delete meta.fieldType;
        }
      }
    }
    return meta;
  }

  public static mergeDefault(defaultMeta: FieldMetadata, meta: FieldMetadata) {
    Object.keys(defaultMeta).forEach(p => {
      //type fieldKeyType = keyof typeof FieldMetadata;
      var k: keyof FieldMetadata = p as keyof FieldMetadata;
      if (meta[k] == null && defaultMeta[k] != null) {
        meta[k] = defaultMeta[k] as never;
      }
    });
  }

  public static hasBaseFieldId(viewMetadata: ViewMetadata): boolean {
    return (viewMetadata?.baseFields?.includes('id'));
  }

  public static hasBaseFieldNum(viewMetadata: ViewMetadata): boolean {
    return (viewMetadata?.baseFields?.includes('num'));
  }

  public static hasBaseFieldName(viewMetadata: ViewMetadata): boolean {
    return (viewMetadata?.baseFields?.includes('name'));
  }

  public static hasBaseFieldSorting(viewMetadata: ViewMetadata): boolean {
    return (viewMetadata?.baseFields?.includes('sorting'));
  }

  public static hasBaseFieldHistory(viewMetadata: ViewMetadata): boolean {
    return (viewMetadata?.baseFields?.includes('history'));
  }

  public static metaInit(view: ViewMetadata, fields: FieldMetadata[]): FieldMetadata[] {
    if (view == null || fields == null) {
      return fields;
    }
    var noTitleFields = (view?.titleFields?.length ?? 0) == 0;
    if (FieldMetaDefaults.hasBaseFieldName(view)) {
      fields = FieldMetaDefaults.addBaseFields(fields, [FieldTypeSettings.name_]);
      if (noTitleFields) {
        view.titleFields ??= [];
        view.titleFields.push('name');
      }
    }
    if (FieldMetaDefaults.hasBaseFieldNum(view)) {
      if (view?.textSingular != null) {
        var fieldMetadataNumText: FieldMetadata = { ...FieldTypeSettings.num };
        fieldMetadataNumText.text = view.textSingular;
        fieldMetadataNumText.tooltipText = view.textSingular + ' unik kode';
        fields = FieldMetaDefaults.addBaseFields(fields, [fieldMetadataNumText]);
      }
      else {
        fields = FieldMetaDefaults.addBaseFields(fields, [FieldTypeSettings.num]);
      }
      if (noTitleFields) {
        view.titleFields ??= [];
        view.titleFields.push('num');
      }

    }
    if (FieldMetaDefaults.hasBaseFieldId(view)) {
      fields = FieldMetaDefaults.addBaseFields(fields, [FieldTypeSettings.id]);
    }

    if (FieldMetaDefaults.hasBaseFieldSorting(view)) {
      fields = FieldMetaDefaults.addBaseFields(fields, [FieldTypeSettings.sorting], 'end');
    }


    if (FieldMetaDefaults.hasBaseFieldHistory(view)) {
      fields = FieldMetaDefaults.addBaseFields(fields, [FieldTypeSettings.created, FieldTypeSettings.modified, FieldTypeSettings.createdBy, FieldTypeSettings.modifiedBy], 'end');
    }

    if (fields?.length > 0) {
      fields.forEach(f => {
        FieldMetaDefaults.init(f);
        // Expand foreignKeyType
        FieldMetaForeignKey.init(view, f);
      });
    }
    return fields;
  }

  public static addBaseFields(fieldMetadata: FieldMetadata[], baseFieldMetadata: FieldMetadata[], position: 'start' | 'end' = 'start') {
    for (var baseField of baseFieldMetadata) {
      var found: boolean = false;
      if (baseField.name && fieldMetadata != null) {
        fieldMetadata.filter(f => f.name == baseField.name).forEach(
          f => {
            //var visible = f.visible;
            //f = baseField;
            //f.visible = visible;
            found = true;
          }
        );
      }
      if (!found) {
        if (fieldMetadata == null) {
          fieldMetadata = [baseField];
        }
        else {
          if (position == 'start') {
            fieldMetadata.unshift(baseField);
          }
          else {
            fieldMetadata.push(baseField);
          }
        }
      }
    }
    return fieldMetadata;
  }
}

export class FieldMetaForeignKey {

  static init(view: ViewMetadata, meta: FieldMetadata, resetTypeSetup: boolean = false): FieldMetadata {
    if (meta.foreignKeyType != null) {
      var typeSetup: FieldMetadata = ForeignKeyTypeSettings[meta.foreignKeyType];
      if (typeSetup != null && typeSetup.foreignKeyTableName != view?.dataApiName) {
        FieldMetaDefaults.mergeDefault(typeSetup, meta);
        if (resetTypeSetup) {
          delete meta.foreignKeyType;
        }
      }
    }
    return meta;
  }

  static dataIsStatic(field: FieldMetadata): boolean {
    return /*(field.foreignKeySubject) || */ (field?.foreignKeyTableName != null && field.foreignKeyTableName.length > 0 && field.foreignKey?.dataOptions?.cache != 'dynamic');
  }

  static lookupValue(field: FieldMetadata, value: any): any {
    if (field && value) {
      var fkData: any[] = FieldMetaForeignKey.dataGet(field);
      if (fkData?.length > 0) {
        var keyField = FieldMetaForeignKey.keyFieldGet(field);
        var valueField = FieldMetaForeignKey.valueFieldGet(field);
        if (keyField?.length > 0 && valueField?.length > 0) {
          var row: any = fkData.find(row => { return row[keyField] == value });
          if (row) {
            var fkValue: any = row[valueField];
            return fkValue;
          }
        }
      }
    }
    return null;
  }

  static keyFieldGet(field: FieldMetadata): string {
    return field.foreignKeyField ?? 'id';
  }

  static valueFieldGet(field: FieldMetadata): string {
    return field.foreignKeyValue ?? 'name';
  }

  static navigationNameGet(field: FieldMetadata): string {
    return field.foreignKeyFromNavigationName ?? field.name + 'Navigation';
  }

  static dataGet(field: FieldMetadata): any[] {
    return field.foreignKey?.data;
  }

  static dataSet(field: FieldMetadata, fkData: any[]) {
    field.foreignKey ??= {};
    field.foreignKey.data = fkData;
    if (field.foreignKey?.dataOptions?.subject) {
      field.foreignKey.dataOptions.subject.next(fkData);
    }
  }

  // Maintain foreign key data rows from navigation property on primary data rows
  static dataMergeFromNavigationFields(fields: FieldMetadata[], relatedData: any[]) {
    if (relatedData?.length > 0) {
      fields.filter(field => FieldMetaForeignKey.dataFromRowNavigation(field))
        .forEach(field => FieldMetaForeignKey.dataFromNavigationFieldMerge(field, relatedData));
    }
  }

  private static dataFromRowNavigation(field: FieldMetadata): boolean {
    return field.foreignKey?.dataOptions?.maintainFromRowNavigation === true;
  }

  private static dataFromNavigationFieldMerge(field: FieldMetadata, relatedData: any[]) {
    if (relatedData != null && relatedData.length > 0) {
      var navigationName: string = FieldMetaForeignKey.navigationNameGet(field);
      var key: string = FieldMetaForeignKey.keyFieldGet(field);
      if (navigationName?.length > 0 && key?.length > 0) {
        relatedData.forEach(relatedRow => FieldMetaForeignKey.dataMergeRow(field, relatedRow[navigationName], key));
      }
    }
  }

  static dataMergeRow(field: FieldMetadata, row: any, key: string) {
    if (field && row) {
      if (!field.foreignKey?.data) {
        field.foreignKey ??= {};
        field.foreignKey.data = [row];
      }
      else {
        var keyValue: any = row[key];
        if (keyValue) {
          var index = field.foreignKey.data.findIndex(item => item[key] == keyValue);
          if (index == -1) {
            field.foreignKey.data.unshift(row);
          }
          else {
            field.foreignKey.data[index] = row;
          }
        }
      }
    }
  }

  public static navigationSetValues(edit: CerGridEditService, row: any, navigationData: any, navigationName: string) {
    if (row != null && row[navigationName] != null) {
      navigationData ??= {};
      const navigationKeys = Object.keys(Object.assign(row[navigationName], navigationData));
      row[navigationName] = navigationData;
      //const navKeys = Object.keys(navigationData);
      //Object.keys(row[navigationName])/*.filter(rowKey => navKeys.includes(rowKey))*/
      navigationKeys.forEach(name => FieldMetaForeignKey.navigationSetValue(edit, name, navigationName, navigationData));
    }
  }

  private static navigationSetValue(edit: CerGridEditService, name: string, navigationName: string, navigationData: any) {
    var value = (navigationData != null ? navigationData[name] : null) ?? null;
    if (value == null) {
      var meta: FieldMetadata = edit.fieldMetadata(navigationName + '.' + name);
      if (meta?.defaultValue != null) {
        value = meta.defaultValue;
      };
    }
    const fieldName = navigationName + '.' + name;
    edit.fieldValueSet(fieldName, value);
  }
}
